import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { DollarSign, TrendingUp, TrendingDown, Activity } from 'lucide-react';

// Import extracted components
import {
  PortfolioCharts,
  PortfolioDealsTable,
  PortfolioStatusBreakdown,
  PortfolioPerformanceMetrics,
  PortfolioDefaultMetrics,
  PortfolioDefaultDealsTable,
  PortfolioPayoffRatio
} from '../components/portfolio';
import { formatCurrency } from '../lib/formatters';
import { useTenant } from '../contexts/TenantContext';
import PortfolioDigestCard from '../components/dashboard/PortfolioDigestCard';
import DigestTrends from '../components/dashboard/DigestTrends';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const { getAuthHeaders, user } = useAuth();
  const { tenant } = useTenant();
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [portfolioStats, setPortfolioStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [dateRange, setDateRange] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [funderFilter, setFunderFilter] = useState('all');
  const [brokerFilter, setBrokerFilter] = useState('all');
  const [settlementMethodFilter, setSettlementMethodFilter] = useState('all');
  const [wentToDefaultFilter, setWentToDefaultFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [funders, setFunders] = useState([]);
  const [brokers, setBrokers] = useState([]);

  // Check user role
  const isAdmin = user?.roles?.includes('admin');
  const isBroker = user?.roles?.includes('broker') && !isAdmin;
  const isFunder = user?.roles?.includes('funder') && !isAdmin;

  const fetchData = useCallback(async () => {
    try {
      // Fetch portfolio stats for accurate totals
      const statsRes = await axios.get(`${API}/reports/portfolio-stats`, { headers: getAuthHeaders() });
      setPortfolioStats(statsRes.data);
      
      // Fetch deals for filtering and table display (limited to 100 for performance)
      const dealsRes = await axios.get(`${API}/deals`, { headers: getAuthHeaders() });
      setDeals(dealsRes.data);
      setFilteredDeals(dealsRes.data);
      
      try {
        const contactsRes = await axios.get(`${API}/contacts`, { headers: getAuthHeaders() });
        const fundersData = contactsRes.data.filter(c => c.is_funder);
        const brokersData = contactsRes.data.filter(c => c.is_broker);
        setFunders(fundersData);
        setBrokers(brokersData);
      } catch (contactError) {
        const uniqueFunders = [...new Map(dealsRes.data
          .filter(d => d.funder_name)
          .map(d => [d.funder_id, { id: d.funder_id, company_name: d.funder_name }])
        ).values()];
        
        const uniqueBrokers = [...new Map(dealsRes.data
          .filter(d => d.broker_name)
          .map(d => [d.broker_id, { id: d.broker_id, company_name: d.broker_name }])
        ).values()];
        
        setFunders(uniqueFunders);
        setBrokers(uniqueBrokers);
      }
    } catch (error) {
      console.error('Failed to fetch portfolio data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const applyFilters = useCallback(() => {
    let filtered = [...deals];

    if (dateRange !== 'all') {
      const now = new Date();
      let startFilterDate;

      switch (dateRange) {
        case 'today':
          startFilterDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          startFilterDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startFilterDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        case 'quarter':
          startFilterDate = new Date(now.setMonth(now.getMonth() - 3));
          break;
        case 'year':
          startFilterDate = new Date(now.setFullYear(now.getFullYear() - 1));
          break;
        case 'custom':
          if (startDate && endDate) {
            filtered = filtered.filter(d => {
              const fundingDate = new Date(d.funding_date);
              return fundingDate >= new Date(startDate) && fundingDate <= new Date(endDate);
            });
          }
          break;
        default:
          break;
      }

      if (dateRange !== 'custom' && startFilterDate) {
        filtered = filtered.filter(d => new Date(d.funding_date) >= startFilterDate);
      }
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }

    if (funderFilter !== 'all') {
      filtered = filtered.filter(d => d.funder_id === funderFilter);
    }

    if (brokerFilter !== 'all') {
      filtered = filtered.filter(d => d.broker_id === brokerFilter);
    }

    if (settlementMethodFilter !== 'all') {
      filtered = filtered.filter(d => d.settlement_method === settlementMethodFilter);
    }

    if (wentToDefaultFilter !== 'all') {
      const want = wentToDefaultFilter === 'yes';
      filtered = filtered.filter(d => d.went_to_default === want);
    }

    setFilteredDeals(filtered);
  }, [deals, dateRange, statusFilter, funderFilter, brokerFilter, settlementMethodFilter, wentToDefaultFilter, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const calculateMetrics = () => {
    // Use portfolioStats for totals when no filters applied
    const noFiltersApplied = dateRange === 'all' && statusFilter === 'all' && funderFilter === 'all' && brokerFilter === 'all';
    
    if (noFiltersApplied && portfolioStats) {
      const { financials, status_breakdown } = portfolioStats;
      const totalFunded = financials.total_funded || 0;
      const totalCollected = financials.total_collected || 0;            // NET
      const totalGrossPayments = financials.total_gross_payments || 0;
      const totalBounces = financials.total_bounces || 0;
      const totalPayback = financials.total_payback || 0;
      const totalBalance = financials.total_balance || 0;
      const totalProfit = totalCollected - totalFunded;
      const avgROI = totalFunded > 0 ? ((totalProfit / totalFunded) * 100) : 0;
      const collectionRate = totalPayback > 0 ? ((totalCollected / totalPayback) * 100) : 0;

      const statusCounts = {
        'Funded': status_breakdown['Funded'] || 0,
        'Payment Issues': status_breakdown['Payment Issues'] || 0,
        'In-House Collection': status_breakdown['In-House Collection'] || 0,
        'Default': status_breakdown['Default'] || 0,
        'Collected': status_breakdown['Collected'] || 0,
        'Paid in Full': status_breakdown['Paid in Full'] || 0
      };

      return {
        totalDeals: financials.total_deals || 0,
        totalFunded,
        totalPayback,
        totalCollected,
        totalGrossPayments,
        totalBounces,
        totalBalance,
        totalProfit,
        avgROI,
        collectionRate,
        statusCounts
      };
    }

    // Use filtered deals when filters are applied
    const totalDeals = filteredDeals.length;
    const totalFunded = filteredDeals.reduce((sum, d) => sum + (d.funding_amount || 0), 0);
    const totalPayback = filteredDeals.reduce((sum, d) => sum + (d.payback || 0), 0);
    const totalCollected = filteredDeals.reduce((sum, d) => sum + (d.payments_made || 0), 0);
    const totalBalance = filteredDeals.reduce((sum, d) => sum + (d.balance || 0), 0);
    const totalProfit = totalCollected - totalFunded;
    const avgROI = totalFunded > 0 ? ((totalProfit / totalFunded) * 100) : 0;
    const collectionRate = totalPayback > 0 ? ((totalCollected / totalPayback) * 100) : 0;

    const statusCounts = {
      'Funded': 0,
      'Payment Issues': 0,
      'In-House Collection': 0,
      'Default': 0,
      'Collected': 0,
      'Paid in Full': 0
    };

    filteredDeals.forEach(d => {
      if (statusCounts.hasOwnProperty(d.status)) {
        statusCounts[d.status]++;
      }
    });

    return {
      totalDeals,
      totalFunded,
      totalPayback,
      totalCollected,
      totalBalance,
      totalProfit,
      avgROI,
      collectionRate,
      statusCounts
    };
  };

  const getCollectionTrendsData = () => {
    const monthlyData = {};
    
    filteredDeals.forEach(deal => {
      const date = new Date(deal.funding_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: new Date(date.getFullYear(), date.getMonth(), 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          funded: 0,
          collected: 0
        };
      }
      
      monthlyData[monthKey].funded += deal.funding_amount;
      monthlyData[monthKey].collected += deal.payments_made;
    });
    
    return Object.values(monthlyData).sort((a, b) => 
      new Date(a.month) - new Date(b.month)
    );
  };

  const renderCustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-sm font-bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const metrics = calculateMetrics();

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isBroker ? "Broker's Portfolio" : "Portfolio Overview"}
          </h1>
          <p className="text-gray-600">
            {isBroker
              ? "View performance of deals you brokered"
              : "Comprehensive view of your deal portfolio performance"}
          </p>
        </div>

        {/* 10/07 (Josh): yesterday's portfolio digest + the funded-vs-collected trend
            moved here from the admin dashboard. Admin only — the digest endpoint is
            admin-gated and the series is the live book. */}
        {isAdmin && (
          <>
            <PortfolioDigestCard getAuthHeaders={getAuthHeaders} tenant={tenant} />
            <DigestTrends getAuthHeaders={getAuthHeaders} tenant={tenant} />
          </>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Date Range</Label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last Month</SelectItem>
                    <SelectItem value="quarter">Last Quarter</SelectItem>
                    <SelectItem value="year">Last Year</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {dateRange === 'custom' && (
                <>
                  <div>
                    <Label>Start Date</Label>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </div>
                </>
              )}

              <div>
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Funded">Funded</SelectItem>
                    <SelectItem value="Payment Issues">Payment Issues</SelectItem>
                    <SelectItem value="In-House Collection">In-House Collection</SelectItem>
                    <SelectItem value="Default">Default</SelectItem>
                    <SelectItem value="Collected">Collected</SelectItem>
                    <SelectItem value="Paid in Full">Paid in Full</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Only show Funder/Broker filters for admin */}
              {isAdmin && (
                <>
                  <div>
                    <Label>Funder</Label>
                    <Select value={funderFilter} onValueChange={setFunderFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Funders</SelectItem>
                        {funders.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Broker</Label>
                    <Select value={brokerFilter} onValueChange={setBrokerFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Brokers</SelectItem>
                        {brokers.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Settlement Method</Label>
                    <Select value={settlementMethodFilter} onValueChange={setSettlementMethodFilter}>
                      <SelectTrigger data-testid="portfolio-settlement-method-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="Attorney/Legal">Attorney/Legal</SelectItem>
                        <SelectItem value="In-House">In-House</SelectItem>
                        <SelectItem value="Broker/ISO">Broker/ISO</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Went to Default?</Label>
                    <Select value={wentToDefaultFilter} onValueChange={setWentToDefaultFilter}>
                      <SelectTrigger data-testid="portfolio-went-to-default-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary KPIs - Adjusted for brokers */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isBroker ? 'lg:grid-cols-2' : 'lg:grid-cols-4'} gap-4 mb-6`}>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Activity className="h-4 w-4 mr-2" />
                Total Deals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{metrics.totalDeals}</div>
              <p className="text-xs text-gray-500">{isBroker ? "Deals you brokered" : "Active portfolio"}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <DollarSign className="h-4 w-4 mr-2" />
                Total Funded
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{formatCurrency(metrics.totalFunded)}</div>
              <p className="text-xs text-gray-500">{isBroker ? "On your deals" : "Capital deployed"}</p>
            </CardContent>
          </Card>

          {/* Only show Total Collected and Outstanding Balance for non-brokers */}
          {!isBroker && (
            <>
              <Card data-testid="portfolio-net-collected-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Net Collected
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">{formatCurrency(metrics.totalCollected)}</div>
                  {metrics.totalBounces > 0 ? (
                    <div className="mt-1 text-xs space-y-0.5">
                      <div className="text-gray-500">Gross: {formatCurrency(metrics.totalGrossPayments)}</div>
                      <div className="text-red-600">Bounces: −{formatCurrency(metrics.totalBounces)}</div>
                      <div className="text-gray-500">{metrics.collectionRate.toFixed(1)}% of expected</div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">{metrics.collectionRate.toFixed(1)}% of expected</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                    <TrendingDown className="h-4 w-4 mr-2" />
                    Outstanding Balance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600">{formatCurrency(metrics.totalBalance)}</div>
                  <p className="text-xs text-gray-500">Remaining to collect</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Performance Metrics - Using extracted component */}
        <PortfolioPerformanceMetrics user={user} filteredDeals={filteredDeals} metrics={metrics} />

        {/* Early Payoff vs Refinance Ratio */}
        <PortfolioPayoffRatio payoffBreakdown={portfolioStats?.payoff_breakdown} />

        {/* Default & Recovery Metrics */}
        {isAdmin && <PortfolioDefaultMetrics getAuthHeaders={getAuthHeaders} />}

        {/* Defaulted Deals List */}
        {isAdmin && <PortfolioDefaultDealsTable getAuthHeaders={getAuthHeaders} />}

        {/* Status Breakdown - Using extracted component */}
        <PortfolioStatusBreakdown metrics={metrics} />

        {/* Charts - Using extracted component */}
        <PortfolioCharts
          filteredDeals={filteredDeals}
          metrics={metrics}
          getCollectionTrendsData={getCollectionTrendsData}
          renderCustomPieLabel={renderCustomPieLabel}
        />

        {/* Deals Table - Using extracted component */}
        <PortfolioDealsTable filteredDeals={filteredDeals} />
      </div>
    </Layout>
  );
};

export default Portfolio;
